/**
 * Whiteboard Main Entry Point
 * static/js/whiteboard/index.js
 * 
 * 初始化 Excalidraw 白板應用
 */

import * as state from './state.js';
import * as api from './api.js';
import * as ui from './ui.js';
import * as autosave from './autosave.js';
import * as collaboration from './collaboration.js';
import * as timer from './timer.js';
import * as randomPicker from './random-picker.js';
import { exportToPng, exportToSvg, exportToJson, getSceneJson } from './export.js';
import { openImportDialog, loadSceneToExcalidraw, getFilenameAsTitle } from './import.js';
import { generateThumbnail } from './thumbnail.js';
import { EXCALIDRAW_CONFIG, MESSAGES } from './config.js';

// 全域變數
let excalidrawComponent = null;
let reactRoot = null;
let initialized = false;

/**
 * 初始化白板應用
 */
export async function init() {
  // 防止重複初始化
  if (initialized) {
    console.log('Whiteboard already initialized, skipping...');
    return;
  }
  initialized = true;
  
  console.log('Initializing Whiteboard...');

  // 設定 loading
  ui.setLoadingState(true);

  try {
    // 載入 React 和 Excalidraw
    await loadExcalidrawLibrary();

    // 掛載 Excalidraw
    await mountExcalidraw();

    // 載入白板列表
    await loadWhiteboardList();

    // 綁定事件
    bindEventListeners();

    // 取得或建立草稿
    await initializeDraft();

    // 自動儲存預設關閉，由使用者手動開啟
    // 綁定自動儲存切換按鈕
    bindAutosaveToggle();

    // 初始化協作功能
    await collaboration.init();
    
    // 綁定協作按鈕
    bindCollaborationButtons();

    console.log('Whiteboard initialized successfully');
  } catch (error) {
    console.error('Failed to initialize whiteboard:', error);
    ui.showToast('初始化失敗：' + error.message, 'error');
  } finally {
    ui.setLoadingState(false);
  }
}

/**
 * 載入 Excalidraw 庫
 */
async function loadExcalidrawLibrary() {
  // Excalidraw 應該已經透過 script 標籤載入
  if (typeof window.ExcalidrawLib === 'undefined') {
    // 等待 Excalidraw 載入
    await new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 秒
      
      const checkInterval = setInterval(() => {
        attempts++;
        if (typeof window.ExcalidrawLib !== 'undefined') {
          clearInterval(checkInterval);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          reject(new Error('Excalidraw library not loaded'));
        }
      }, 100);
    });
  }
  
  console.log('Excalidraw library loaded');
}

/**
 * 掛載 Excalidraw 元件
 */
async function mountExcalidraw() {
  const container = document.getElementById('excalidraw-container');
  if (!container) {
    throw new Error('Excalidraw container not found');
  }

  const React = window.React;
  const ReactDOM = window.ReactDOM;
  const { Excalidraw } = window.ExcalidrawLib;

  // 建立 React 元件
  const App = () => {
    const [excalidrawAPI, setExcalidrawAPI] = React.useState(null);

    React.useEffect(() => {
      if (excalidrawAPI) {
        state.setExcalidrawAPI(excalidrawAPI);
        // 設定初始 hash
        const elements = excalidrawAPI.getSceneElements();
        state.setLastSavedHash(state.hashElements(elements));
      }
    }, [excalidrawAPI]);

    return React.createElement(Excalidraw, {
      excalidrawAPI: (api) => setExcalidrawAPI(api),
      theme: EXCALIDRAW_CONFIG.theme,
      langCode: EXCALIDRAW_CONFIG.langCode,
      initialData: {
        elements: [],
        appState: {
          viewBackgroundColor: EXCALIDRAW_CONFIG.viewBackgroundColor,
        },
      },
      onChange: (elements, appState, files) => {
        autosave.onSceneChange(elements, appState);
      },
      UIOptions: {
        canvasActions: {
          loadScene: false, // 使用自訂載入
          export: false,    // 使用自訂匯出
          saveToActiveFile: false,
        },
      },
    });
  };

  // 掛載 React
  if (ReactDOM.createRoot) {
    reactRoot = ReactDOM.createRoot(container);
    reactRoot.render(React.createElement(App));
  } else {
    ReactDOM.render(React.createElement(App), container);
  }

  // 等待 API 就緒
  await new Promise((resolve) => {
    const checkAPI = setInterval(() => {
      if (state.getExcalidrawAPI()) {
        clearInterval(checkAPI);
        resolve();
      }
    }, 50);
  });

  console.log('Excalidraw mounted');
}

/**
 * 載入白板列表
 */
async function loadWhiteboardList() {
  try {
    const response = await api.listWhiteboards();
    if (response.success) {
      state.setWhiteboards(response.whiteboards || []);
      ui.updateWhiteboardList(
        state.getWhiteboards(),
        state.getCurrentDrawingId()
      );
    }
  } catch (error) {
    console.error('Failed to load whiteboard list:', error);
  }
}

/**
 * 初始化草稿
 */
async function initializeDraft() {
  try {
    const response = await api.getOrCreateDraft();
    if (response.success && response.draft) {
      const draft = response.draft;
      state.setCurrentDrawingId(draft.id);
      state.setCurrentTitle(draft.title);

      // 載入草稿內容
      if (draft.scene_json) {
        try {
          const sceneData = JSON.parse(draft.scene_json);
          loadSceneToExcalidraw(sceneData);
        } catch (e) {
          console.warn('Failed to parse draft scene:', e);
        }
      }
    }
  } catch (error) {
    console.error('Failed to initialize draft:', error);
  }
}

/**
 * 綁定事件監聽器
 */
function bindEventListeners() {
  // 新建按鈕
  const newBtn = document.getElementById('btn-new');
  if (newBtn) {
    newBtn.addEventListener('click', handleNew);
  }

  // 儲存按鈕
  const saveBtn = document.getElementById('btn-save');
  if (saveBtn) {
    saveBtn.addEventListener('click', handleSave);
  }

  // 匯出按鈕
  const exportPngBtn = document.getElementById('btn-export-png');
  if (exportPngBtn) {
    exportPngBtn.addEventListener('click', () => exportToPng());
  }

  const exportSvgBtn = document.getElementById('btn-export-svg');
  if (exportSvgBtn) {
    exportSvgBtn.addEventListener('click', () => exportToSvg());
  }

  const exportJsonBtn = document.getElementById('btn-export-json');
  if (exportJsonBtn) {
    exportJsonBtn.addEventListener('click', () => exportToJson());
  }

  // 匯入按鈕
  const importBtn = document.getElementById('btn-import');
  if (importBtn) {
    importBtn.addEventListener('click', handleImport);
  }

  // 白板列表點擊
  const listContainer = document.getElementById('whiteboard-list');
  if (listContainer) {
    listContainer.addEventListener('click', handleWhiteboardClick);
  }

  // 標題輸入
  const titleInput = document.getElementById('whiteboard-title');
  if (titleInput) {
    titleInput.addEventListener('change', (e) => {
      state.setCurrentTitle(e.target.value.trim() || '未命名白板');
    });
  }

  // 刪除按鈕
  const deleteBtn = document.getElementById('btn-delete');
  if (deleteBtn) {
    deleteBtn.addEventListener('click', handleDelete);
  }

  // 重新整理列表
  const refreshBtn = document.getElementById('btn-refresh-list');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', loadWhiteboardList);
  }

  // 頁面卸載前儲存
  window.addEventListener('beforeunload', (e) => {
    if (state.isDirty()) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // 計時器按鈕
  const timerBtn = document.getElementById('btn-timer');
  console.log('Timer button found:', timerBtn);
  if (timerBtn) {
    // 初始化計時器
    console.log('Initializing timer...');
    timer.initTimer();
    console.log('Timer initialized, widget exists:', !!document.getElementById('timer-widget'));
    
    timerBtn.addEventListener('click', () => {
      console.log('Timer button clicked');
      timer.toggleTimer();
      console.log('Timer visible after toggle:', timer.isTimerVisible());
      if (timer.isTimerVisible()) {
        timerBtn.classList.add('active');
      } else {
        timerBtn.classList.remove('active');
      }
    });
  }
  
  // 隨機抽籤按鈕
  const pickerBtn = document.getElementById('btn-random-picker');
  console.log('Picker button found:', pickerBtn);
  if (pickerBtn) {
    // 初始化抽籤器
    console.log('Initializing random picker...');
    randomPicker.initRandomPicker();
    console.log('Picker initialized, widget exists:', !!document.getElementById('picker-widget'));
    
    pickerBtn.addEventListener('click', () => {
      console.log('Picker button clicked');
      randomPicker.togglePicker();
      console.log('Picker visible after toggle:', randomPicker.isPickerVisible());
      if (randomPicker.isPickerVisible()) {
        pickerBtn.classList.add('active');
      } else {
        pickerBtn.classList.remove('active');
      }
    });
  }
}

/**
 * 處理新建
 */
async function handleNew() {
  // 確認是否有未儲存變更
  if (state.isDirty()) {
    const confirm = await ui.showConfirm('目前有未儲存的變更，確定要新建嗎？');
    if (!confirm) return;
  }

  try {
    // 取得新草稿
    const response = await api.getOrCreateDraft();
    if (response.success && response.draft) {
      const draft = response.draft;
      
      // 重置 Excalidraw
      const excalidrawAPI = state.getExcalidrawAPI();
      if (excalidrawAPI) {
        excalidrawAPI.resetScene();
      }

      state.resetState();
      state.setCurrentDrawingId(draft.id);
      state.setCurrentTitle('未命名白板');

      // 更新 UI
      const titleInput = document.getElementById('whiteboard-title');
      if (titleInput) {
        titleInput.value = '未命名白板';
      }

      ui.updateAutosaveIndicator('saved');
      ui.showToast('已建立新白板', 'success');
    }
  } catch (error) {
    console.error('Failed to create new whiteboard:', error);
    ui.showToast(MESSAGES.SAVE_FAILED, 'error');
  }
}

/**
 * 處理儲存
 */
async function handleSave() {
  const drawingId = state.getCurrentDrawingId();
  if (!drawingId) {
    ui.showToast('請先建立白板', 'warning');
    return;
  }

  // 取得標題
  let title = state.getCurrentTitle();
  if (!title || title === '未命名白板') {
    const inputTitle = await ui.showPrompt('請輸入白板標題', title);
    if (inputTitle === null) return; // 取消
    title = inputTitle || '未命名白板';
    state.setCurrentTitle(title);
  }

  try {
    ui.setLoadingState(true);

    // 取得場景 JSON（先儲存最新的場景資料，包含圖片）
    const sceneJson = getSceneJson();
    if (sceneJson) {
      // 先儲存草稿，確保最新的 scene（包括圖片）被保存
      await api.saveDraft(drawingId, sceneJson);
    }

    // 生成縮圖
    const thumbnail = await generateThumbnail();

    // 儲存（提升草稿為正式）
    const response = await api.promoteDraft(drawingId, title, thumbnail);

    if (response.success) {
      state.setDirty(false);
      ui.updateAutosaveIndicator('saved');
      ui.showToast(MESSAGES.SAVE_SUCCESS, 'success');

      // 更新 hash
      const excalidrawAPI = state.getExcalidrawAPI();
      if (excalidrawAPI) {
        const elements = excalidrawAPI.getSceneElements();
        state.setLastSavedHash(state.hashElements(elements));
      }

      // 重新載入列表
      await loadWhiteboardList();
    } else {
      throw new Error(response.error || MESSAGES.SAVE_FAILED);
    }
  } catch (error) {
    console.error('Save failed:', error);
    ui.showToast(error.message || MESSAGES.SAVE_FAILED, 'error');
  } finally {
    ui.setLoadingState(false);
  }
}

/**
 * 處理匯入
 */
async function handleImport() {
  try {
    const result = await openImportDialog();
    if (!result) return;

    // 載入到 Excalidraw
    const success = loadSceneToExcalidraw(result);
    if (success) {
      state.setDirty(true);
      ui.updateAutosaveIndicator('dirty');
      ui.showToast(MESSAGES.IMPORT_SUCCESS, 'success');
    }
  } catch (error) {
    console.error('Import failed:', error);
    ui.showToast(MESSAGES.IMPORT_FAILED, 'error');
  }
}

/**
 * 處理白板列表點擊
 */
async function handleWhiteboardClick(e) {
  const item = e.target.closest('.whiteboard-item');
  if (!item) return;

  const id = parseInt(item.dataset.id, 10);
  if (!id || id === state.getCurrentDrawingId()) return;

  // 確認是否有未儲存變更
  if (state.isDirty()) {
    const confirm = await ui.showConfirm('目前有未儲存的變更，確定要載入其他白板嗎？');
    if (!confirm) return;
  }

  try {
    ui.setLoadingState(true);

    const response = await api.getWhiteboard(id);
    if (response.success && response.whiteboard) {
      const whiteboard = response.whiteboard;

      // 解析場景
      let sceneData = { elements: [], appState: { viewBackgroundColor: '#ffffff' }, files: {} };
      if (whiteboard.scene_json) {
        try {
          sceneData = JSON.parse(whiteboard.scene_json);
        } catch (e) {
          console.warn('Failed to parse scene:', e);
        }
      }

      // 載入到 Excalidraw
      loadSceneToExcalidraw(sceneData);

      // 更新狀態
      state.setCurrentDrawingId(whiteboard.id);
      state.setCurrentTitle(whiteboard.title);
      state.setDirty(false);

      // 更新 hash
      const excalidrawAPI = state.getExcalidrawAPI();
      if (excalidrawAPI) {
        const elements = excalidrawAPI.getSceneElements();
        state.setLastSavedHash(state.hashElements(elements));
      }

      // 更新 UI
      const titleInput = document.getElementById('whiteboard-title');
      if (titleInput) {
        titleInput.value = whiteboard.title;
      }

      ui.updateWhiteboardList(state.getWhiteboards(), id);
      ui.updateAutosaveIndicator('saved');
    }
  } catch (error) {
    console.error('Failed to load whiteboard:', error);
    ui.showToast(MESSAGES.LOAD_FAILED, 'error');
  } finally {
    ui.setLoadingState(false);
  }
}

/**
 * 處理刪除
 */
async function handleDelete() {
  const drawingId = state.getCurrentDrawingId();
  if (!drawingId) {
    ui.showToast('沒有可刪除的白板', 'warning');
    return;
  }

  const confirm = await ui.showConfirm(MESSAGES.DELETE_CONFIRM);
  if (!confirm) return;

  try {
    ui.setLoadingState(true);

    const response = await api.deleteWhiteboard(drawingId);
    if (response.success) {
      state.removeWhiteboard(drawingId);
      ui.showToast(MESSAGES.DELETE_SUCCESS, 'success');

      // 建立新草稿
      await handleNew();

      // 重新載入列表
      await loadWhiteboardList();
    } else {
      throw new Error(response.error || MESSAGES.DELETE_FAILED);
    }
  } catch (error) {
    console.error('Delete failed:', error);
    ui.showToast(error.message || MESSAGES.DELETE_FAILED, 'error');
  } finally {
    ui.setLoadingState(false);
  }
}

/**
 * 取得自動儲存間隔
 */
function getAutosaveInterval() {
  const container = document.getElementById('excalidraw-container');
  if (container && container.dataset.autosaveInterval) {
    return parseInt(container.dataset.autosaveInterval, 10) || 10;
  }
  return 10;
}

/**
 * 綁定自動儲存切換按鈕
 */
function bindAutosaveToggle() {
  const toggleBtn = document.getElementById('btn-autosave-toggle');
  if (!toggleBtn) return;

  toggleBtn.addEventListener('click', () => {
    const isEnabled = autosave.isAutosaveEnabled();
    const newState = !isEnabled;
    
    // 切換狀態
    autosave.setAutosaveEnabled(newState, getAutosaveInterval());
    
    // 更新按鈕樣式
    if (newState) {
      toggleBtn.classList.add('active');
      toggleBtn.title = '自動儲存（開啟）';
    } else {
      toggleBtn.classList.remove('active');
      toggleBtn.title = '自動儲存';
    }
  });
}

/**
 * 綁定協作按鈕
 */
function bindCollaborationButtons() {
  // 開始協作按鈕
  const startCollabBtn = document.getElementById('btn-start-collab');
  if (startCollabBtn) {
    startCollabBtn.addEventListener('click', async () => {
      const drawingId = state.getCurrentDrawingId();
      await collaboration.createRoom(drawingId, true);
    });
  }
  
  // 離開協作按鈕
  const leaveCollabBtn = document.getElementById('btn-leave-collab');
  if (leaveCollabBtn) {
    leaveCollabBtn.addEventListener('click', () => {
      collaboration.leaveRoom();
    });
  }
  
  // 雷射筆按鈕
  const laserBtn = document.getElementById('btn-laser-pointer');
  if (laserBtn) {
    laserBtn.addEventListener('click', () => {
      const isActive = collaboration.toggleLaserPointer();
      if (isActive) {
        laserBtn.classList.add('laser-active');
        laserBtn.title = '關閉雷射筆';
        ui.showToast('雷射筆已啟用', 'info');
      } else {
        laserBtn.classList.remove('laser-active');
        laserBtn.title = '雷射筆';
      }
    });
  }
  
}

/**
 * 清理（頁面卸載時）
 */
export function cleanup() {
  autosave.stopAutosave();
  if (reactRoot) {
    reactRoot.unmount();
  }
}

// 暴露給全域
window.WhiteboardApp = {
  init,
  cleanup,
  exportToPng,
  exportToSvg,
  exportToJson,
};

// 等待 Excalidraw 載入完成後初始化
function waitForExcalidraw() {
  // 如果 Excalidraw 已經載入，直接初始化
  if (typeof window.ExcalidrawLib !== 'undefined') {
    init();
    return;
  }
  
  // 否則等待 excalidraw-ready 事件
  window.addEventListener('excalidraw-ready', init, { once: true });
  
  // 設定超時作為備用
  setTimeout(() => {
    if (typeof window.ExcalidrawLib !== 'undefined') {
      init();
    }
  }, 10000);
}

// DOM Ready 後開始等待 Excalidraw
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForExcalidraw);
} else {
  waitForExcalidraw();
}
